/**
 * @file index.tsx
 *
 * @fileoverview Organization search page. Performs the graphql query to
 * search for organizations.
 *
 * Passes information to this layout such as which facets to render, which
 * results where returned and also how many items per page to render.
 */

import React, { useContext, useEffect, useMemo } from 'react';
import { Layout } from '~/jobiqo/components/Layout';
import { FacetConfig } from '~/jobiqo/models/search';
import { PathContextInterface } from '~/jobiqo/models/app';
import { PathContext } from '~/jobiqo/context/app';
import Pager from '../Pager';
import { FormattedMessage, useIntl } from 'react-intl';
import Link from 'next/link';
import { SearchMetatags } from '../Search/search-metatags';
import { OrganizationFiltersSection } from './FiltersSection';
import { useSearch } from '../Search/hooks/useSearch';
import { OrganizationTeaser } from '~/jobiqo/components/Organization/Teaser';
import LazyHydrate from 'react-lazy-hydration';
import Image from '~/jobiqo/components/Image';
import SEOSearchTitle from '../Search/SeoSearchTitle';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { Box } from '~/vcl/atoms/07-box';
import { EmptyElement } from '~/vcl/molecules/02-empty';
import { SearchLocationsIcon } from '~/vcl/icons/SearchLocations';
import { OrganizationSearchPlaceholder } from '~/vcl/organisms/05-placeholders/06-organization/04-search';
import { HeroRow } from '~/vcl/organisms/04-landingpage/02-hero-row';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { Grid } from '~/vcl/atoms/06-layout/04-grid';
import { Cell } from '~/vcl/atoms/06-layout/04-grid/01-cell';
import { VerticallyCentered } from '~/vcl/atoms/06-layout/01-alignment/03-vertical-align';
import { AlignedCenter } from '~/vcl/atoms/06-layout/01-alignment/01-align-center';
import { OfficeIcon } from '~/vcl/icons/Office';
import { UndoAltSolidIcon } from '~/vcl/icons/UndoAltSolid';
import SEOSearchFooter from '~/jobiqo/components/Search/SeoSearchFooter';
import OrganizationSearchMetatags from './OrganizationSearchMetatags';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { analyticsDispatch, EVENTS } from '~/jobiqo/lib/analytics';
import { getOrganizationSearchDimensions } from '~/jobiqo/components/OrganizationSearch/utils/organization_search_analytics';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { getSearchLabel } from '~/jobiqo/components/Search/search-title';
import { Metatag } from '~/jobiqo/types/types';
import { getGeoInputFromUrl } from '~/jobiqo/components/Search/utils/searchHelpers';

interface OrganizationsSearchPageProps {
  querystring: any;
  loading: boolean;
  error: boolean;
  data: any;
}

export const OrganizationSearch = ({
  querystring,
  error,
  data,
}: OrganizationsSearchPageProps) => {
  const pathContext: PathContextInterface = useContext(PathContext);
  const userContext: UserContextInterface = useContext(UserContext);
  const intl = useIntl();
  const { ORGANIZATIONS } = useFrontendConfig();
  const organizationsSearchFacetsConfig: FacetConfig[] = ORGANIZATIONS.FACETS;
  const SEOHeaderImage = useMemo(
    () => data?.organizationProfiles?.seoSearchContent?.headerImage,
    [data?.organizationProfiles?.seoSearchContent?.headerImage],
  );

  const {
    getFilterSelectedItems,
    toggleFilter,
    getFilterSelectedFacets,
  } = useSearch({
    searchUrl: ORGANIZATIONS.ORGANIZATIONS_SEARCH_PAGE,
    cleanFacetsOrder: ORGANIZATIONS.SEO.CLEAN_FACETS_ORDER,
    cleanFacetsNumber: ORGANIZATIONS.SEO.CLEAN_FACETS_NUMBER,
  });

  // Returns all organizationProfiles data, including facets.
  const organizationProfiles = data?.organizationProfiles ?? null;

  // Trigger tracking event for organization profile search.
  useEffect(() => {
    if (organizationProfiles) {
      analyticsDispatch(
        EVENTS.ORGANIZATION_PROFILE_SEARCH,
        getOrganizationSearchDimensions(
          userContext.user,
          organizationProfiles.facets
            ? getFilterSelectedFacets(querystring, organizationProfiles.facets)
            : [],
          organizationProfiles?.seoSearchContent?.title ??
            organizationProfiles?.seoSearchContent?.subtitle ??
            getSearchLabel({
              metadata: organizationProfiles.metatags as Metatag[],
              type: 'organization profile',
              amount: organizationProfiles.result_count ?? 0,
              intl,
            }),
          organizationProfiles,
          getGeoInputFromUrl(querystring).label ?? '',
          querystring.search,
        ),
      );
    }
  }, [querystring]);

  if (error) {
    return (
      <LazyHydrate whenIdle={true}>
        <Layout mainContentSize="small">
          <Main>
            <Box>
              <EmptyElement
                icon={
                  <SearchLocationsIcon
                    color="danger"
                    height="250"
                    width="250"
                    title="Search"
                  />
                }
              >
                <p>
                  <FormattedMessage
                    id="organization_load_organizations_error"
                    defaultMessage={`Something went wrong with the current search.
                  There was an error loading organization profiles. Feel free to contact us regarding this
                  problem or start a new search.`}
                  />
                </p>
                <p>
                  <Link href={ORGANIZATIONS.ORGANIZATIONS_SEARCH_PAGE}>
                    <FormattedMessage
                      id="organization_start_new_search"
                      defaultMessage="Start a new search"
                    />
                  </Link>
                </p>
              </EmptyElement>
            </Box>
          </Main>
        </Layout>
      </LazyHydrate>
    );
  }

  return (
    <Layout fullWidthContainer={true}>
      {!organizationProfiles ? (
        <Main>
          <OrganizationSearchPlaceholder />
        </Main>
      ) : (
        <>
          <OrganizationSearchMetatags
            organizationProfiles={organizationProfiles}
          />
          <SearchMetatags metadata={organizationProfiles.metatags} />
          {SEOHeaderImage ? (
            <HeroRow
              boxPosition="background"
              image={
                <Image
                  src={SEOHeaderImage.url}
                  alt={SEOHeaderImage.alt}
                  {...(SEOHeaderImage.title && {
                    title: SEOHeaderImage.title,
                  })}
                  {...(SEOHeaderImage.caption && {
                    caption: SEOHeaderImage.caption,
                  })}
                  width={null}
                  height={null}
                  priority={true}
                />
              }
            >
              <Main>
                <Text
                  textAlign="center"
                  tag="h1"
                  lineHeight={[6, 8]}
                  fontSize={[6, 8]}
                >
                  {organizationProfiles?.seoSearchContent?.title}
                </Text>
                <Spacing size="medium" />
                <Text textAlign="center" fontSize={4} fontWeight="bold">
                  {organizationProfiles?.seoSearchContent?.subtitle}
                </Text>
                <Spacing size="large" />
                <OrganizationFiltersSection
                  pathname={pathContext.pathname}
                  querystring={querystring}
                  facets={organizationProfiles.facets}
                  getFilterSelectedItems={getFilterSelectedItems}
                  toggleFilter={toggleFilter}
                  hiddenForm={false}
                  organizationSearchFacetsConfig={
                    organizationsSearchFacetsConfig
                  }
                />
              </Main>
            </HeroRow>
          ) : (
            <Main>
              <Spacing size="large" />
              <OrganizationFiltersSection
                pathname={pathContext.pathname}
                querystring={querystring}
                facets={organizationProfiles.facets}
                getFilterSelectedItems={getFilterSelectedItems}
                toggleFilter={toggleFilter}
                hiddenForm={false}
                organizationSearchFacetsConfig={organizationsSearchFacetsConfig}
              />
            </Main>
          )}
          <Spacing size="large" />
          <Main>
            {organizationProfiles?.pages?.length > 0 ? (
              <>
                <FlexItems alignItems="center">
                  <FlexItem marginRight={6}>
                    <SEOSearchTitle
                      title={intl.formatMessage({
                        id: 'organization_search_title_type',
                        defaultMessage: 'organization',
                      })}
                      searchResult={organizationProfiles}
                    />
                  </FlexItem>
                </FlexItems>
                <Spacing size="medium" />
                <Grid columns={[1, 2, 3]}>
                  {organizationProfiles.pages.map((organization) => (
                    <Cell key={organization.id} display="grid">
                      <OrganizationTeaser
                        key={organization.id}
                        organization={organization}
                      />
                    </Cell>
                  ))}
                </Grid>
                <Spacing />
                {organizationProfiles.result_count >
                  ORGANIZATIONS.ORGANIZATIONS_SEARCH_LISTING_NUMBER && (
                  <AlignedCenter>
                    <Pager
                      items={organizationProfiles.result_count}
                      itemsPerPage={
                        ORGANIZATIONS.ORGANIZATIONS_SEARCH_LISTING_NUMBER
                      }
                      querystring={querystring}
                    />
                  </AlignedCenter>
                )}
              </>
            ) : (
              <VerticallyCentered>
                <Box>
                  <EmptyElement
                    icon={<OfficeIcon title="office" />}
                    iconWidth="300"
                    iconHeight="180"
                    iconColor="primary"
                  >
                    <Spacing />
                    <FormattedMessage
                      id="organization_search_empty_search"
                      defaultMessage={`No organizations were found for the selected criteria.`}
                    />
                    <Spacing size="medium" />
                    <Link href={ORGANIZATIONS.ORGANIZATIONS_SEARCH_PAGE}>
                      <>
                        <UndoAltSolidIcon
                          aria-hidden="true"
                          width="15"
                          height="15"
                          title="Undo"
                        />
                        &nbsp;
                        <FormattedMessage
                          id={'organization_search_current_search_reset'}
                          defaultMessage={'Reset Search'}
                        />
                      </>
                    </Link>
                  </EmptyElement>
                </Box>
              </VerticallyCentered>
            )}
          </Main>
        </>
      )}
      {organizationProfiles?.seoSearchContent?.footer ? (
        <Main size="xLarge">
          <Box padding={9}>
            <SEOSearchFooter
              id="footer"
              format={organizationProfiles?.seoSearchContent?.footerFormat}
              content={
                organizationProfiles?.seoSearchContent?.footer
                  ? organizationProfiles?.seoSearchContent?.footer
                  : ''
              }
            />
          </Box>
        </Main>
      ) : null}
      <Spacing marginTop={[8, 12]} />
    </Layout>
  );
};

export default OrganizationSearch;
